import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Chip,
  Spinner,
} from '@/components/ui';
import { StandardIcon } from '@/components/common/icon';
import { useApiWithAuth } from '@/hooks/api';
import { makePayloadRequestInit } from '@/lib/api';
import type { ApiResponse } from '@/shared/types/api';
import type {
  PcoSyncCategory,
  PcoSyncRequestCategory,
  PcoSyncRunResult,
  PcoSyncStatus,
} from '@/shared/types/pcoSync';
import { formatDistanceToNow } from 'date-fns';
import { useCallback, useEffect, useRef, useState } from 'react';

const categoryDetails: Record<
  PcoSyncCategory,
  { description: string; icon: string }
> = {
  campuses: {
    description: 'Planning Center campuses and location metadata',
    icon: 'mdi:map-marker-outline',
  },
  people: {
    description: 'People records and profile details',
    icon: 'mdi:account-multiple-outline',
  },
  households: {
    description: 'Households and household memberships',
    icon: 'mdi:home-heart',
  },
  groups: {
    description: 'Group types, groups, leaders, and memberships',
    icon: 'mdi:account-group-outline',
  },
  services: {
    description: 'Service types, teams, leaders, and memberships',
    icon: 'mdi:calendar-account-outline',
  },
};

function formatTimestamp(value: string | null) {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return {
    relative: formatDistanceToNow(date, { addSuffix: true }),
    exact: new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(date),
  };
}

export function PcoSync() {
  const { auth, invoke } = useApiWithAuth();
  const invokeRef = useRef(invoke);
  invokeRef.current = invoke;
  const [status, setStatus] = useState<PcoSyncStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [requestedCategory, setRequestedCategory] =
    useState<PcoSyncRequestCategory | null>(null);
  const [notice, setNotice] = useState<{
    color: 'success' | 'danger' | 'warning';
    text: string;
  } | null>(null);

  const loadStatus = useCallback(async () => {
    if (!auth.ready) {
      return;
    }

    setLoadError('');
    try {
      const response = (await invokeRef.current('/platform/pco-sync')) as
        | ApiResponse<PcoSyncStatus | null>
        | string;
      if (
        typeof response === 'string' ||
        !response.data ||
        !Array.isArray(response.data.categories)
      ) {
        throw new Error(
          typeof response === 'string'
            ? response
            : response.message || 'Unable to load PCO sync status',
        );
      }

      setStatus(response.data);
    } catch (error) {
      console.error('Failed to load PCO sync status:', error);
      setLoadError(
        error instanceof Error
          ? error.message
          : 'Unable to load PCO sync status',
      );
    } finally {
      setLoading(false);
    }
  }, [auth.ready]);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  const startSync = async (category: PcoSyncRequestCategory) => {
    setRequestedCategory(category);
    setNotice(null);

    try {
      const response = (await invokeRef.current(
        '/platform/pco-sync',
        makePayloadRequestInit({ category }),
      )) as ApiResponse<PcoSyncRunResult | null> | string;
      if (typeof response === 'string' || !response.data) {
        throw new Error(
          typeof response === 'string'
            ? response
            : response.message || 'PCO sync failed',
        );
      }

      const failedSteps = response.data.steps.filter((step) => !step.success);
      setNotice({
        color: response.data.success ? 'success' : 'warning',
        text: response.data.success
          ? `${category === 'all' ? 'All PCO categories' : categoryDetails[category].description} synced successfully.`
          : `Sync completed with errors in ${failedSteps
              .map((step) => categoryDetails[step.category].description)
              .join(', ')}.`,
      });
    } catch (error) {
      console.error('Failed to run PCO sync:', error);
      setNotice({
        color: 'danger',
        text:
          error instanceof Error
            ? error.message
            : 'Unable to start the PCO sync',
      });
    } finally {
      setRequestedCategory(null);
      await loadStatus();
    }
  };

  const isRunning = Boolean(requestedCategory || status?.runningCategory);

  return (
    <div className='flex flex-col gap-5 py-4'>
      <div className='flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between'>
        <div>
          <h2 className='text-xl font-semibold text-slate-900'>PCO Sync</h2>
          <p className='mt-1 max-w-2xl text-sm text-slate-500'>
            Monitor Planning Center sync checkpoints and run an incremental sync
            for every category or a single category.
          </p>
        </div>
        <div className='flex shrink-0 gap-2'>
          <Button
            variant='flat'
            onPress={() => void loadStatus()}
            isDisabled={loading || isRunning}
            startContent={<StandardIcon id='mdi:refresh' size={18} />}
          >
            Refresh
          </Button>
          <Button
            color='primary'
            onPress={() => void startSync('all')}
            isLoading={requestedCategory === 'all'}
            isDisabled={!status?.configured || isRunning}
            startContent={<StandardIcon id='mdi:sync' size={18} />}
          >
            Sync all
          </Button>
        </div>
      </div>

      {notice ? (
        <Alert color={notice.color} title='PCO sync'>
          {notice.text}
        </Alert>
      ) : null}
      {loadError ? (
        <Alert color='danger' title='Unable to load sync status'>
          {loadError}
        </Alert>
      ) : null}
      {status && !status.configured ? (
        <Alert color='warning' title='Planning Center is not configured'>
          Add the PCO client ID and secret before running a sync.
        </Alert>
      ) : null}

      <Card>
        <CardHeader className='flex flex-wrap items-center justify-between gap-3'>
          <div>
            <div className='font-semibold text-slate-900'>Sync categories</div>
            <div className='mt-0.5 text-xs text-slate-500'>
              Tenant: {status?.tenantId || '—'}
            </div>
          </div>
          {status ? (
            <Chip
              className={
                status.configured
                  ? 'bg-emerald-50 text-emerald-700'
                  : 'bg-amber-50 text-amber-800'
              }
            >
              {status.configured ? 'Configured' : 'Not configured'}
            </Chip>
          ) : null}
        </CardHeader>
        <CardBody className='p-0'>
          {loading ? (
            <div className='flex items-center justify-center gap-3 px-4 py-16 text-sm text-slate-500'>
              <Spinner size='sm' /> Loading sync status…
            </div>
          ) : null}
          {!loading && status
            ? status.categories.map((category, index) => {
                const timestamp = formatTimestamp(category.lastSyncedAt);
                const categoryRunning =
                  requestedCategory === category.category ||
                  requestedCategory === 'all' ||
                  status.runningCategory === category.category ||
                  status.runningCategory === 'all';
                const details = categoryDetails[category.category];
                let syncState = {
                  label: 'No history',
                  className: 'bg-slate-100 text-slate-500',
                };
                if (timestamp) {
                  syncState = {
                    label: 'Completed',
                    className: 'bg-emerald-50 text-emerald-700',
                  };
                }
                if (categoryRunning) {
                  syncState = {
                    label: 'Syncing',
                    className: 'bg-blue-50 text-blue-700',
                  };
                }

                return (
                  <div
                    key={category.category}
                    className={`grid gap-4 px-4 py-4 sm:grid-cols-[minmax(0,1fr)_minmax(180px,0.7fr)_auto] sm:items-center ${
                      index > 0 ? 'border-t border-slate-100' : ''
                    }`}
                  >
                    <div className='flex min-w-0 items-start gap-3'>
                      <span className='flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-700'>
                        <StandardIcon id={details.icon} size={21} />
                      </span>
                      <div className='min-w-0'>
                        <div className='flex flex-wrap items-center gap-2'>
                          <span className='font-semibold text-slate-900'>
                            {category.label}
                          </span>
                          <Chip className={syncState.className}>
                            {syncState.label}
                          </Chip>
                        </div>
                        <div className='mt-0.5 text-sm text-slate-500'>
                          {details.description}
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className='text-xs font-medium uppercase tracking-wide text-slate-400'>
                        Last completed sync
                      </div>
                      {timestamp ? (
                        <div className='mt-1'>
                          <div className='text-sm font-medium text-slate-800'>
                            {timestamp.relative}
                          </div>
                          <div className='text-xs text-slate-500'>
                            {timestamp.exact}
                          </div>
                        </div>
                      ) : (
                        <div className='mt-1 text-sm text-slate-400'>
                          Never synced
                        </div>
                      )}
                    </div>
                    <Button
                      variant='ghost'
                      onPress={() => void startSync(category.category)}
                      isLoading={requestedCategory === category.category}
                      isDisabled={!status.configured || isRunning}
                      startContent={<StandardIcon id='mdi:sync' size={17} />}
                    >
                      Sync now
                    </Button>
                  </div>
                );
              })
            : null}
        </CardBody>
      </Card>
    </div>
  );
}
